import api from "./api";
import { APIS } from "../endpoints";

// Returns true when the filter payload has no user-applied selections.
// Backend treats any non-empty `filters` query param as a filtered query and
// returns an empty result, so we OMIT the param entirely on initial loads
// (default state: empty filters object + default sortBy_v + empty search).
const _isDefaultFilterOptions = (opts) => {
  if (!opts || typeof opts !== "object") return true;
  const filters = opts.filters;
  const hasFilterValues =
    filters &&
    typeof filters === "object" &&
    Object.values(filters).some((v) =>
      Array.isArray(v) ? v.length > 0 : Boolean(v),
    );
  if (hasFilterValues) return false;
  if (opts.search) return false;
  return true;
};

/**
 * This function returns bibliography data for a masterlist's research centre library
 * @param {String} alias - Alias of Masterlist
 * @param {String} masterlistType - Masterlist Type (e.g., "person-actor", "film")
 * @param {Object} filterOptions - Filter options including filters, search, sortBy_v, gate
 * @param {Number} page - Page number for pagination
 * @returns {Promise<Object>} - Function returns bibliography data with facets and pagination info
 */
export const bibliographyByMasterlist = async (
  alias,
  masterlistType,
  filterOptions = {},
  page = 1,
) => {
  try {
    const params = new URLSearchParams();

    // Add filters (omit entirely when default — backend treats any non-empty
    // `filters` param as an active filtered query)
    if (!_isDefaultFilterOptions(filterOptions) && filterOptions.filters) {
      params.append("filters", JSON.stringify(filterOptions.filters));
    }

    // Add page number
    params.append("page_number", page);

    // Add search term if present
    if (filterOptions.search) {
      params.append("search", filterOptions.search);
    }

    // Add sort option if present
    if (filterOptions.sortBy_v) {
      params.append("sortBy", filterOptions.sortBy_v);
    }

    // Add gate (AND/OR) if present
    if (filterOptions.gate) {
      params.append("gate", filterOptions.gate);
    }

    const result = await api.get(
      `${APIS.BASE_URL}${masterlistType}/${alias}/research-centre/library?${params.toString()}`,
    );

    if (result?.data) {
      return result.data;
    } else {
      return {
        data: [],
        total: 0,
        facets: {},
        facetsHeaders: [],
        sortByHeader: [],
      };
    }
  } catch (error) {
    console.error("Error fetching bibliography data:", error);
    throw error;
  }
};

/**
 * This function returns archive data for a masterlist's research centre archive
 * @param {String} alias - Alias of Masterlist
 * @param {String} masterlistType - Masterlist Type (e.g., "person-actor", "film")
 * @param {Object} filterOptions - Filter options including filters, search, sortBy_v, gate
 * @param {Number} page - Page number for pagination
 * @returns {Promise<Object>} - Function returns archive data with facets and pagination info
 */
export const archiveByMasterlist = async (
  alias,
  masterlistType,
  filterOptions = {},
  page = 1,
) => {
  try {
    const params = new URLSearchParams();

    // Add filters (omit entirely when default — backend treats any non-empty
    // `filters` param as an active filtered query)
    if (!_isDefaultFilterOptions(filterOptions) && filterOptions.filters) {
      params.append("filters", JSON.stringify(filterOptions.filters));
    }

    // Add page number
    params.append("page_number", page);

    // Add search term if present
    if (filterOptions.search) {
      params.append("search", filterOptions.search);
    }

    // Add sort option if present
    if (filterOptions.sortBy_v) {
      params.append("sortBy", filterOptions.sortBy_v);
    }

    // Add gate (AND/OR) if present
    if (filterOptions.gate) {
      params.append("gate", filterOptions.gate);
    }

    const result = await api.get(
      `${APIS.BASE_URL}${masterlistType}/${alias}/research-centre/archive?${params.toString()}`,
    );

    if (result?.data) {
      return result.data;
    } else {
      return {
        data: [],
        total: 0,
        facets: {},
        facetsHeaders: [],
        sortByHeader: [],
      };
    }
  } catch (error) {
    console.error("Error fetching archive data:", error);
    throw error;
  }
};

/**
 * Update library highlight status
 * @param {String} alias - Library alias
 * @param {Boolean} isHighlighted - Whether the library should be highlighted
 * @returns {Promise<Object>} - API response
 */
export const updateLibraryHighlight = async (
  alias,
  isHighlighted,
  masterlistAlias,
) => {
  try {
    const highlightValue = isHighlighted ? "yes" : "no";
    const result = await api.put(
      `${APIS.BASE_URL}admin/libraries/${alias}/highlight?highlight=${highlightValue}&masterlist_alias=${masterlistAlias}`,
    );

    if (result?.data) {
      return result.data;
    } else {
      return [];
    }
  } catch (error) {
    console.error("Error updating library highlight:", error);
    throw error;
  }
};

/**
 * Update archive highlight status
 * @param {String} alias - Archive alias
 * @param {Boolean} isHighlighted - Whether the archive should be highlighted
 * @returns {Promise<Object>} - API response
 */
export const updateArchiveHighlight = async (
  alias,
  isHighlighted,
  masterlistAlias,
) => {
  try {
    const highlightValue = isHighlighted ? "yes" : "no";
    const result = await api.put(
      `${APIS.BASE_URL}admin/archive/${alias}/highlight?highlight=${highlightValue}&masterlist_alias=${masterlistAlias}`,
    );

    if (result?.data) {
      return result.data;
    } else {
      return [];
    }
  } catch (error) {
    console.error("Error updating archive highlight:", error);
    throw error;
  }
};
